// FMO Revenue - Override tracking, projections, by carrier/agency
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, DollarSign, AlertTriangle, BarChart3, PieChart } from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_REVENUE_TOUR_STEPS = [
  { type: 'splash', title: 'Revenue Dashboard', description: 'Track commission revenue across your entire FMO — total earnings, first-year vs renewal splits, revenue at risk, and breakdowns by agency and carrier.' },
  { target: 'fmo-revenue-summary', emoji: '💰', title: 'Revenue Summary', description: 'Four key cards: Total Revenue YTD, First-Year commissions, Renewal commissions, and Revenue at Risk. The at-risk number shows how much you could lose from at-risk clients.', position: 'bottom' },
  { target: 'fmo-revenue-byagency', emoji: '🏢', title: 'By Agency', description: 'Revenue bars per agency. Instantly see which agencies generate the most revenue — and where to invest in retention to protect your biggest earners.', position: 'right' },
  { target: 'fmo-revenue-bycarrier', emoji: '🏥', title: 'By Carrier', description: 'Revenue split by carrier with client counts. Use this for carrier contract negotiations — show your total production to secure better override rates.', position: 'left' },
  { type: 'celebration', title: 'Revenue Intelligence!', description: 'Revenue at risk is your most actionable metric. Every dollar protected through retention is a dollar you don\'t have to replace with new production.' },
];

function formatCurrency(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
}

export default function FmoRevenue() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [revTourStep, setRevTourStep] = useState(0);
  const [showRevTour, setShowRevTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-revenue-tour-dismissed')) setShowRevTour(true);
  }, []);
  const advanceRevTour = useCallback(() => { setRevTourStep(s => { if (s + 1 >= FMO_REVENUE_TOUR_STEPS.length) { localStorage.setItem('fmo-revenue-tour-dismissed', 'true'); setShowRevTour(false); return 0; } return s + 1; }); }, []);
  const backRevTour = useCallback(() => setRevTourStep(s => Math.max(0, s - 1)), []);
  const dismissRevTour = useCallback(() => { localStorage.setItem('fmo-revenue-tour-dismissed', 'true'); setShowRevTour(false); }, []);
  useEffect(() => {
    if (!showRevTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissRevTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceRevTour(); if (e.key === 'ArrowLeft') backRevTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showRevTour, advanceRevTour, backRevTour, dismissRevTour]);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/revenue`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) { console.error('[FMO Revenue]', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRevenue(); }, [fetchRevenue]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div>;

  const maxAgency = Math.max(...(data?.byAgency || []).map(a => a.total), 1);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A] flex items-center gap-2"><DollarSign className="text-green-600" /> Revenue</h1>
              <p className="text-sm text-gray-500">Commission tracking and revenue analysis</p>
            </div>
          </div>
          <button onClick={fetchRevenue} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={18} className="text-gray-500" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-tour="fmo-revenue-summary">
          <div className="bg-white rounded-xl border-t-4 border-green-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">Total Revenue (YTD)</p>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(data?.totalRevenue)}</p>
          </div>
          <div className="bg-white rounded-xl border-t-4 border-blue-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">First Year</p>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(data?.firstYear)}</p>
          </div>
          <div className="bg-white rounded-xl border-t-4 border-purple-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">Renewal</p>
            <p className="text-3xl font-bold text-purple-600">{formatCurrency(data?.renewal)}</p>
          </div>
          <div className="bg-white rounded-xl border-t-4 border-red-500 shadow-sm p-5">
            <p className="text-sm text-gray-500 flex items-center gap-1"><AlertTriangle size={14} /> At Risk</p>
            <p className="text-3xl font-bold text-red-600">{formatCurrency(data?.revenueAtRisk)}</p>
            <p className="text-xs text-gray-400">{data?.atRiskClients || 0} clients</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Agency */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-revenue-byagency">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><BarChart3 size={18} /> By Agency</h3>
            <div className="space-y-3">
              {(data?.byAgency || []).map((a, i) => (
                <div key={a.agencyId}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{a.agencyName}</span>
                    <span className="text-sm font-bold text-green-600">{formatCurrency(a.total)}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: `${(a.total / maxAgency) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Carrier */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-revenue-bycarrier">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><PieChart size={18} /> By Carrier</h3>
            <div className="space-y-3">
              {(data?.byCarrier || []).slice(0, 10).map(c => {
                const totalRev = (data?.byCarrier || []).reduce((s, x) => s + x.total, 0) || 1;
                return (
                  <div key={c.carrier} className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-700">{c.carrier}</span>
                      <span className="text-xs text-gray-400 ml-2">{c.clientCount} clients</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-gray-900">{formatCurrency(c.total)}</span>
                      <span className="text-xs text-gray-400 ml-1">({Math.round((c.total / totalRev) * 100)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showRevTour && (
        <TourBubble stepData={FMO_REVENUE_TOUR_STEPS[revTourStep]} stepIndex={revTourStep} totalSteps={FMO_REVENUE_TOUR_STEPS.length}
          onNext={advanceRevTour} onBack={backRevTour} onDismiss={dismissRevTour}
          splashIcon={<DollarSign className="w-10 h-10 text-green-600" />} maskId="fmo-revenue-tour-mask" />
      )}
    </div>
  );
}
